import { useState, useRef, useCallback } from 'react';

interface UseHoverTouchResult {
  isHovered: boolean;
  hoverProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
}

export const useHoverTouch = (
  longPressDelay = 5000,
  touchEndDelay = 3000,
): UseHoverTouchResult => {
  const [isHovered, setIsHovered] = useState(false);
  const touchTimeout = useRef<NodeJS.Timeout | null>(null);
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault(); // Prevent unwanted behaviors like text selection
      setIsHovered(true);

      if (touchTimeout.current) {
        clearTimeout(touchTimeout.current);
      }

      longPressTimeout.current = setTimeout(() => {
        setIsHovered(false);
      }, longPressDelay);
    },
    [longPressDelay],
  );

  const handleTouchEnd = useCallback(() => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
    }

    touchTimeout.current = setTimeout(() => {
      setIsHovered(false);
    }, touchEndDelay);
  }, [touchEndDelay]);

  const hoverProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  };

  return { isHovered, hoverProps };
};
